import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
// Локали нужны только для названий месяцев («29 июля» / «July 29»); остальные
// форматы числовые и от локали dayjs не зависят.
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import { currentLanguage, DEFAULT_LANG, type Lang } from '@/shared/i18n';

dayjs.extend(customParseFormat);

// Даты в кабинете живут в двух разных ролях, и путать их нельзя.
//
// 1. ТРАНСПОРТ — WIRE_DATE_FORMAT. В этом виде период лежит в состоянии фильтров
//    статистики и в этом же виде его парсят адаптеры запросов
//    (entities/statistics/api/*). Формат фиксированный и от языка не зависит:
//    смена локали не должна ломать запрос. Раньше эту строку получали через
//    `toLocaleDateString('ru-RU')` — работало, но выглядело как отображение и
//    напрашивалось на «локализацию», которая сломала бы статистику.
//
// 2. ОТОБРАЖЕНИЕ — formatDate / formatDateTime / formatTime. Вот это по языку и
//    меняется: RU — 02.09.2026, EN — Sep 2, 2026.
//
// Время в обоих языках 24-часовое: партнёры сверяют время подписки с логами и
// выгрузками, где оно тоже 24-часовое, а «2:30 PM» в такой сверке только мешает.

export const WIRE_DATE_FORMAT = 'DD.MM.YYYY';

const FORMATS: Record<
  Lang,
  { date: string; dateTime: string; time: string; dayMonth: string; dayMonthYear: string }
> = {
  ru: {
    date: 'DD.MM.YYYY',
    dateTime: 'DD.MM.YYYY HH:mm',
    time: 'HH:mm',
    dayMonth: 'D MMMM',
    dayMonthYear: 'D MMMM YYYY',
  },
  en: {
    date: 'MMM D, YYYY',
    dateTime: 'MMM D, YYYY HH:mm',
    time: 'HH:mm',
    dayMonth: 'MMMM D',
    dayMonthYear: 'MMMM D, YYYY',
  },
};

function formats() {
  return FORMATS[currentLanguage()] ?? FORMATS[DEFAULT_LANG];
}

// Битую дату возвращаем как пришла: в таблицах лучше показать сырое значение из
// API, чем «Invalid Date» — так видно, что именно приехало.
function apply(value: string | number | Date | dayjs.Dayjs | null | undefined, pattern: string) {
  if (value === null || value === undefined || value === '') return '';
  const d = dayjs(value);
  // Локаль передаём точечно, а не через глобальный dayjs.locale(): глобальная
  // настройка утекла бы в места, которые обязаны оставаться числовыми.
  return d.isValid() ? d.locale(currentLanguage()).format(pattern) : String(value);
}

export function formatDate(value: string | number | Date | dayjs.Dayjs | null | undefined) {
  return apply(value, formats().date);
}

export function formatDateTime(value: string | number | Date | dayjs.Dayjs | null | undefined) {
  return apply(value, formats().dateTime);
}

export function formatTime(value: string | number | Date | dayjs.Dayjs | null | undefined) {
  return apply(value, formats().time);
}

// «29 июля» / «July 29» — для подписи дня в ленте новостей. Год добавляется
// только для прошлых лет, иначе он шум.
export function formatDayMonth(
  value: string | number | Date | dayjs.Dayjs,
  options: { withYear?: boolean } = {},
) {
  return apply(value, options.withYear ? formats().dayMonthYear : formats().dayMonth);
}

// «2 часа назад», «вчера», дальше недели — «16 сентября»: для подписи «когда
// обновлён» в списке, где важна давность, а не точное время. Будущее (часы
// сервера впереди) считаем «сейчас», а не «через 3 минуты».
export function formatRelative(value: string | number | Date | dayjs.Dayjs, now: Date = new Date()) {
  const d = dayjs(value);
  if (!d.isValid()) return String(value);

  const seconds = Math.max(0, dayjs(now).diff(d, 'second'));
  const rtf = new Intl.RelativeTimeFormat(currentLanguage(), { numeric: 'auto' });

  if (seconds < 60) return rtf.format(0, 'second');
  if (seconds < 3600) return rtf.format(-Math.floor(seconds / 60), 'minute');
  if (seconds < 86400) return rtf.format(-Math.floor(seconds / 3600), 'hour');

  // Дни — по календарю, а не по суткам: позавчерашние 23:00 сегодня в 01:00 —
  // это «позавчера», хотя прошло 26 часов.
  const days = dayjs(now).startOf('day').diff(d.startOf('day'), 'day');
  if (days < 7) return rtf.format(-days, 'day');

  return formatDayMonth(d, { withYear: d.year() !== dayjs(now).year() });
}

// «18 сент.» / «Sep 18» — короткая подпись дня на оси графика, где места на
// месяц полностью нет.
export function formatDayMonthShort(value: string | number | Date | dayjs.Dayjs) {
  return apply(value, currentLanguage() === 'en' ? 'MMM D' : 'D MMM');
}

// Дата в транспортном виде: для записи в состояние фильтров.
export function toWireDate(value: Date | dayjs.Dayjs) {
  return dayjs(value).format(WIRE_DATE_FORMAT);
}

// И обратно — из состояния фильтров в Date для календаря.
export function fromWireDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = dayjs(value, WIRE_DATE_FORMAT);
  return parsed.isValid() ? parsed.toDate() : null;
}
